<template>
  <n-dialog
    v-model:visible="visible"
    class="enterprise-add-member-modal"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
  >
    <form class="add-member-dialog" @submit.prevent="submit">
      <div class="add-member-dialog__header">
        <button class="add-member-dialog__close" type="button" :aria-label="$t('enterprise_management_close')" @click="close">
          <WorkbenchIcon name="x" :size="16" />
        </button>
      </div>
      <h2>{{ isEditing ? $t('enterprise_management_edit_member') : $t('account_enterprise_add_member') }}</h2>

      <div v-if="!isEditing" class="add-member-tabs" role="tablist">
        <button type="button" :class="{ active: activeMode === 'email' }" @click="switchMode('email')">
          {{ $t('enterprise_management_email_add') }}
        </button>
        <button type="button" :class="{ active: activeMode === 'link' }" @click="switchMode('link')">
          {{ $t('enterprise_management_link_invite') }}
        </button>
      </div>

      <template v-if="isEditing || activeMode === 'email'">
        <div class="form-field">
          <label><i>*</i>{{ $t('enterprise_ui_workspace_label') }}</label>
          <div v-if="isEditing || !isMultiWorkspace" class="locked-space">
            <span>
              <WorkbenchIcon :name="form.region === 'VIP' ? 'map-pin' : 'globe'" :size="13" />
              {{ regionLabel(form.region || currentRegion) }}
            </span>
            <WorkbenchIcon name="lock" :size="13" />
          </div>
          <el-select
            v-else
            v-model="form.region"
            class="member-field-control"
            size="large"
            :placeholder="$t('enterprise_management_select_workspace')"
            popper-class="enterprise-member-select-popper enterprise-member-space-popper"
            :teleported="true"
            :disabled="formLocked"
          >
            <el-option v-for="item in regionOptions" :key="item.value" :label="item.label" :value="item.value">
              <div class="enterprise-member-select-option">
                <WorkbenchIcon :name="item.value === 'VIP' ? 'map-pin' : 'globe'" :size="13" />
                <span>{{ item.label }}</span>
                <WorkbenchIcon v-if="form.region === item.value" name="check" :size="12" />
              </div>
            </el-option>
          </el-select>
        </div>
        <p v-if="!isEditing" class="space-hint">
          {{ $t(isMultiWorkspace ? 'enterprise_management_multi_workspace_hint' : 'enterprise_management_single_workspace_hint') }}
        </p>
        <p v-if="legacyPending" class="space-hint legacy-pending-hint">
          {{ $t('enterprise_management_previous_invite_processing_hint') }}
          <button type="button" :disabled="submitting || checkingStatus" @click="checkLegacyPendingStatus">
            {{ checkingStatus ? $t('enterprise_management_querying') : $t('enterprise_management_query_status') }}
          </button>
        </p>

        <div class="form-field">
          <label for="enterprise-member-email"><i>*</i>{{ $t('enterprise_ui_email_label') }}</label>
          <input
            id="enterprise-member-email"
            v-model.trim="form.email"
            type="email"
            maxlength="120"
            :placeholder="$t('enterprise_management_enter_email')"
            autocomplete="off"
            :disabled="isEditing || formLocked"
          />
        </div>

        <div class="email-add-panel">
          <div v-if="emailAddNotice" class="email-add-notice">
            <div class="email-add-notice__message">
              <WorkbenchIcon name="circle-alert" :size="12" />
              <span>{{ emailAddNotice }}</span>
            </div>
            <button v-if="!isEditing && emailAddNoticeSwitchable" type="button" @click="switchMode('link')">
              {{ $t('enterprise_management_switch_link_invite') }} →
            </button>
          </div>

      <div class="form-field">
        <label for="enterprise-member-alias">{{ $t('enterprise_ui_alias_label') }}</label>
        <input id="enterprise-member-alias" v-model.trim="form.alias" type="text" maxlength="12" :placeholder="$t('enterprise_management_enter_value')" autocomplete="off" :disabled="formLocked" />
      </div>

      <div class="form-field">
        <label><i>*</i>{{ $t('enterprise_ui_role_label') }}</label>
        <el-select
          v-model="form.role"
          class="member-field-control"
          size="large"
          :placeholder="$t('enterprise_management_select_value')"
          popper-class="enterprise-member-select-popper"
          :disabled="formLocked || (isEditing && !canEditRole)"
        >
          <el-option v-for="item in roleOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </div>

      <div class="permission-fields">
        <div class="permission-row">
          <span class="permission-row__label">{{ $t('enterprise_ui_permissions_label') }}</span>
          <div class="permission-row__body">
            <span class="permission-row__name">
              {{ $t('enterprise_management_files') }}
              <PermissionHelpTooltip
                kind="file"
                :active="activeHelp === 'file'"
                :pinned="pinnedHelp === 'file'"
                @open="schedulePermissionHelp"
                @keep="keepPermissionHelp"
                @leave="leavePermissionHelp"
                @toggle="togglePermissionHelp"
              />
            </span>
            <label class="permission-option">
              <input v-model="form.fileLook" type="checkbox" :disabled="formLocked" />
              <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
              <span>{{ $t('account_enterprise_permission_look') }}</span>
            </label>
            <label class="permission-option">
              <input v-model="form.fileDownload" type="checkbox" :disabled="formLocked" />
              <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
              <span>{{ $t('account_enterprise_permission_download') }}</span>
            </label>
          </div>
        </div>
        <div class="permission-row">
          <span class="permission-row__label" aria-hidden="true" />
          <div class="permission-row__body">
            <span class="permission-row__name">
              {{ $t('enterprise_management_terms') }}
              <PermissionHelpTooltip
                kind="term"
                :active="activeHelp === 'term'"
                :pinned="pinnedHelp === 'term'"
                @open="schedulePermissionHelp"
                @keep="keepPermissionHelp"
                @leave="leavePermissionHelp"
                @toggle="togglePermissionHelp"
              />
            </span>
            <label class="permission-option">
              <input v-model="form.termUse" type="checkbox" :disabled="formLocked" />
              <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
              <span>{{ $t('account_enterprise_permission_term_use') }}</span>
            </label>
            <label class="permission-option">
              <input v-model="form.termEdit" type="checkbox" :disabled="formLocked" />
              <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
              <span>{{ $t('account_enterprise_permission_edit') }}</span>
            </label>
          </div>
        </div>
      </div>
        <p v-if="!isEditing" class="email-add-rule">
          {{ $t(isMultiWorkspace ? 'enterprise_management_email_add_rule_global' : 'enterprise_management_email_add_rule_single') }}
        </p>
      </div>
      </template>

      <div v-else class="link-invite-panel">
        <p class="link-invite-description">
          {{ $t(isMultiWorkspace ? 'enterprise_management_link_invite_description_global' : 'enterprise_management_link_invite_description_single') }}
        </p>

        <div v-if="isMultiWorkspace" class="form-field">
          <label><i>*</i>{{ $t('enterprise_ui_workspace_label') }}</label>
          <el-select
            v-model="form.region"
            class="member-field-control"
            size="large"
            :placeholder="$t('enterprise_management_select_workspace')"
            popper-class="enterprise-member-select-popper enterprise-member-space-popper"
            :teleported="true"
            :disabled="formLocked"
          >
            <el-option v-for="item in regionOptions" :key="item.value" :label="item.label" :value="item.value">
              <div class="enterprise-member-select-option">
                <WorkbenchIcon :name="item.value === 'VIP' ? 'map-pin' : 'globe'" :size="13" />
                <span>{{ item.label }}</span>
                <WorkbenchIcon v-if="form.region === item.value" name="check" :size="12" />
              </div>
            </el-option>
          </el-select>
        </div>

        <div class="permission-fields">
          <div class="permission-row">
            <span class="permission-row__label">{{ $t('enterprise_ui_permissions_label') }}</span>
            <div class="permission-row__body">
              <span class="permission-row__name">
                {{ $t('enterprise_management_files') }}
                <PermissionHelpTooltip
                  kind="file"
                  :active="activeHelp === 'file'"
                  :pinned="pinnedHelp === 'file'"
                  @open="schedulePermissionHelp"
                  @keep="keepPermissionHelp"
                  @leave="leavePermissionHelp"
                  @toggle="togglePermissionHelp"
                />
              </span>
              <label class="permission-option">
                <input v-model="form.fileLook" type="checkbox" :disabled="formLocked" />
                <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
                <span>{{ $t('account_enterprise_permission_look') }}</span>
              </label>
              <label class="permission-option">
                <input v-model="form.fileDownload" type="checkbox" :disabled="formLocked" />
                <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
                <span>{{ $t('account_enterprise_permission_download') }}</span>
              </label>
            </div>
          </div>
          <div class="permission-row">
            <span class="permission-row__label" aria-hidden="true" />
            <div class="permission-row__body">
              <span class="permission-row__name">
                {{ $t('enterprise_management_terms') }}
                <PermissionHelpTooltip
                  kind="term"
                  :active="activeHelp === 'term'"
                  :pinned="pinnedHelp === 'term'"
                  @open="schedulePermissionHelp"
                  @keep="keepPermissionHelp"
                  @leave="leavePermissionHelp"
                  @toggle="togglePermissionHelp"
                />
              </span>
              <label class="permission-option">
                <input v-model="form.termUse" type="checkbox" :disabled="formLocked" />
                <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
                <span>{{ $t('account_enterprise_permission_term_use') }}</span>
              </label>
              <label class="permission-option">
                <input v-model="form.termEdit" type="checkbox" :disabled="formLocked" />
                <span class="permission-option__checkbox"><WorkbenchIcon name="check" :size="10" /></span>
                <span>{{ $t('account_enterprise_permission_edit') }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="link-email-row">
          <input
            v-model.trim="form.email"
            type="email"
            maxlength="120"
            :placeholder="$t('enterprise_management_enter_email')"
            autocomplete="off"
            :disabled="formLocked"
          />
          <button type="button" :disabled="!canGenerateLink || submitting" @click="createTargetInvite">
            {{ $t('enterprise_management_generate_link') }}
          </button>
        </div>

        <div v-if="linkInviteNotice" class="email-add-notice link-invite-notice">
          <div class="email-add-notice__message">
            <WorkbenchIcon name="circle-alert" :size="12" />
            <span>{{ linkInviteNotice }}</span>
          </div>
        </div>

        <div v-if="generatedInviteUrl" class="generated-link-block">
          <div class="generated-link">
            <input :value="generatedInviteUrl" readonly />
            <button type="button" @click="copyInviteLink">{{ $t('enterprise_management_copy') }}</button>
          </div>
          <div class="generated-link-meta">
            <span>
              <WorkbenchIcon name="clock" :size="12" />
              {{ generatedInviteMeta }}
            </span>
            <button type="button" :disabled="submitting" @click="createTargetInvite">
              {{ $t('enterprise_management_regenerate_link') }}
            </button>
          </div>
        </div>

        <p class="link-invite-notes">
          {{ $t(isMultiWorkspace ? 'enterprise_management_link_invite_notes_global' : 'enterprise_management_link_invite_notes_single', { used: inviteSeatUsed, total: inviteSeatLimit }) }}
        </p>
      </div>

      <button class="save-button" type="submit" :disabled="!canSubmit || submitting || checkingStatus">
        {{ saveButtonText }}
      </button>
    </form>
  </n-dialog>
</template>

<script setup lang="ts">
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import PermissionHelpTooltip from './PermissionHelpTooltip.vue'
import AccountServer from '@/services/AccountServer'
import GlobalBizServer from '@/services/GlobalBizServer'
import {
  EnterpriseMemberPermission,
  PermissionUtils,
  type EnterpriseMember,
  type EnterpriseSite,
  type EnterpriseTargetInvite,
} from '@/services/AccountServer/types'
import useUserStore from '@/store/modules/user'
import { storeToRefs } from 'pinia'
import { NetCode } from '@/types/const'
import Toast from '@/utils/toast'
import { useSiteConfig } from '@/config/useSiteConfig'
import LegacyServer from '@/services/LegacyEnterpriseServer'
import type { LegacyMemberResult } from '@/services/LegacyEnterpriseServer/types'
import { getEnterpriseWorkspaceMode } from '@/utils/enterpriseManagement'
import { useI18n } from 'vue-i18n'
import { onBeforeUnmount } from 'vue'
import {
  clearLegacyMemberPending,
  createLegacyMemberRequestId,
  legacyMemberPendingIdentityKey,
  legacyMemberPendingStorageAvailable,
  legacyMemberStatusState,
  readLegacyMemberPending,
  rememberLegacyMemberPending,
  type LegacyMemberPendingIdentity,
  type LegacyMemberPendingRecord,
} from '@/utils/legacyEnterpriseMemberPending'

const emit = defineEmits<{ (event: 'saved'): void }>()
const userStore = useUserStore()
const { userInfo, bizTeamInfo, userRights } = storeToRefs(userStore)
const { isVipSite } = useSiteConfig()
const { t } = useI18n()
const TEAM_MEMBER_COUNT_LIMIT_CODE = '10035'
const USER_NOT_FOUND_CODE = '1003'
// 后端 EKUserException.BIZ_MEMBER_HAS_OTHER_ERR：被邀请人已加入其他企业
const MEMBER_IN_OTHER_TEAM_CODE = '10045'

const visible = ref(false)
const submitting = ref(false)
const checkingStatus = ref(false)
const activeMode = ref<'email' | 'link'>('email')
const generatedInvite = ref<EnterpriseTargetInvite | null>(null)
const staleGeneratedInvite = ref<EnterpriseTargetInvite | null>(null)
const generatedInviteKey = ref('')
const emailAddNotice = ref('')
// 未注册提示可引导去链接邀请；已加入其他企业时链接邀请同样无效，不展示引导
const emailAddNoticeSwitchable = ref(true)
const linkInviteNotice = ref('')
const editingMember = ref<EditableMember | null>(null)
const legacyPending = ref<LegacyMemberPendingRecord | null>(null)
type PermissionHelpType = 'file' | 'term'
const activeHelp = ref<PermissionHelpType | null>(null)
const pinnedHelp = ref<PermissionHelpType | null>(null)
let dialogRevision = 0
let helpTimer: ReturnType<typeof setTimeout> | null = null
type EditableMember = EnterpriseMember & { region?: EnterpriseSite }
const form = reactive({
  region: '' as EnterpriseSite | '',
  email: '',
  alias: '',
  role: null as number | null,
  fileLook: true,
  fileEdit: true,
  fileDownload: true,
  termUse: true,
  termEdit: true,
})

const currentRegion = computed<EnterpriseSite>(() => isVipSite.value ? 'VIP' : 'AI')
const workspaceMode = computed(() => getEnterpriseWorkspaceMode(bizTeamInfo.value))
const isPaidGlobal = computed(() => workspaceMode.value === 'global')
const isLegacyCross = computed(() => workspaceMode.value === 'legacy-cross')
const isMultiWorkspace = computed(() => isPaidGlobal.value || isLegacyCross.value)
const regionOptions = computed<Array<{ value: EnterpriseSite; label: string }>>(() => [
  { value: 'VIP', label: t('enterprise_management_china_workspace') },
  { value: 'AI', label: t('enterprise_management_international_workspace') },
])
const roleOptions = computed(() => Number(userInfo.value?.bizTeamMemberRole) === 1
  ? [{ value: 2, label: t('account_enterprise_add_member_role_admin') }, { value: 3, label: t('account_enterprise_table_header_member') }]
  : [{ value: 3, label: t('account_enterprise_table_header_member') }])
const isEditing = computed(() => Boolean(editingMember.value))
const canEditRole = computed(() => Number(userInfo.value?.bizTeamMemberRole) === 1)
const formLocked = computed(() => submitting.value)
const canGenerateLink = computed(() => {
  const hasRegion = !isMultiWorkspace.value || Boolean(form.region)
  return hasRegion && /^\S+@\S+\.\S+$/.test(form.email)
})
const generatedInviteUrl = computed(() => String(generatedInvite.value?.url || ''))
const inviteSeatUsed = computed(() => Number(generatedInvite.value?.seatUsed ?? bizTeamInfo.value?.memberCount ?? 0))
const inviteSeatLimit = computed(() => Number(
  generatedInvite.value?.seatLimit
  ?? (isMultiWorkspace.value ? bizTeamInfo.value?.globalSeatLimit : userRights.value?.memberCountLimit)
  ?? 0,
))
const generatedInviteMeta = computed(() => {
  const expireTime = generatedInvite.value?.expireTime
  if (!expireTime) return t('enterprise_management_link_expire_hint')
  return t('enterprise_management_link_valid_until', {
    time: formatDateTime(expireTime),
    remaining: remainingLabel(expireTime),
  })
})
const canSubmit = computed(() => {
  const hasRegion = !isMultiWorkspace.value || Boolean(form.region)
  const hasEmail = /^\S+@\S+\.\S+$/.test(form.email)
  if (!isEditing.value && activeMode.value === 'link') return Boolean(generatedInviteUrl.value)
  return hasRegion && hasEmail && Boolean(form.role)
})
const saveButtonText = computed(() => {
  if (submitting.value) return t('enterprise_management_processing')
  if (!isEditing.value && activeMode.value === 'link') {
    return t('enterprise_management_copy_and_close')
  }
  return isEditing.value ? t('enterprise_management_save') : t('enterprise_management_confirm_add')
})
const pendingIdentity = computed(() => ({
  userId: userInfo.value?.id,
  teamId: bizTeamInfo.value?.id || userInfo.value?.bizTeamId,
  migrationNo: bizTeamInfo.value?.legacyMigrationNo,
  site: currentRegion.value,
}))

interface LegacyMemberSnapshot {
  revision: number
  identity: LegacyMemberPendingIdentity
  identityKey: string
  requestId: string
  targetSite: EnterpriseSite
  createdAt: number
}

interface LegacyStatusHandlingOptions {
  silent?: boolean
  keepDialogOpen?: boolean
}

watch(
  () => [userStore.token, userInfo.value?.id, userInfo.value?.bizTeamId, userInfo.value?.bizTeamMemberRole,
    bizTeamInfo.value?.id, bizTeamInfo.value?.legacyMigrationNo, bizTeamInfo.value?.globalContractNo, workspaceMode.value, currentRegion.value].join(':'),
  () => {
    dialogRevision += 1
    reset()
    visible.value = false
  },
  { flush: 'sync' },
)

watch(
  () => targetInviteKey(),
  (value) => {
    if (generatedInvite.value && generatedInviteKey.value && value !== generatedInviteKey.value) {
      staleGeneratedInvite.value = generatedInvite.value
      generatedInvite.value = null
      generatedInviteKey.value = ''
    }
    linkInviteNotice.value = ''
  },
  { flush: 'sync' },
)

function regionLabel(region: EnterpriseSite) {
  return region === 'VIP' ? t('enterprise_management_china_workspace') : t('enterprise_management_international_workspace')
}

function targetInviteKey() {
  return `${form.region}:${form.email.trim().toLowerCase()}:${targetInvitePermissionValue()}`
}

function clearGeneratedInvite() {
  generatedInvite.value = null
  staleGeneratedInvite.value = null
  generatedInviteKey.value = ''
}

function clearHelpTimer() {
  if (!helpTimer) return
  clearTimeout(helpTimer)
  helpTimer = null
}

function addOutsidePermissionHelpListener() {
  if (typeof document === 'undefined') return
  document.addEventListener('pointerdown', onOutsidePermissionHelpPointerDown, true)
  document.addEventListener('keydown', onPermissionHelpKeyDown)
}

function removeOutsidePermissionHelpListener() {
  if (typeof document === 'undefined') return
  document.removeEventListener('pointerdown', onOutsidePermissionHelpPointerDown, true)
  document.removeEventListener('keydown', onPermissionHelpKeyDown)
}

function closePermissionHelp() {
  clearHelpTimer()
  activeHelp.value = null
  pinnedHelp.value = null
  removeOutsidePermissionHelpListener()
}

function onOutsidePermissionHelpPointerDown(event: PointerEvent) {
  const target = event.target as HTMLElement | null
  if (target?.closest?.('[data-permission-help]')) return
  closePermissionHelp()
}

function onPermissionHelpKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') closePermissionHelp()
}

function activatePermissionHelp(type: PermissionHelpType) {
  activeHelp.value = type
  addOutsidePermissionHelpListener()
}

function schedulePermissionHelp(type: PermissionHelpType) {
  clearHelpTimer()
  if (pinnedHelp.value) return
  helpTimer = setTimeout(() => {
    activatePermissionHelp(type)
  }, 300)
}

function keepPermissionHelp(type: PermissionHelpType) {
  clearHelpTimer()
  if (pinnedHelp.value && pinnedHelp.value !== type) return
  activatePermissionHelp(type)
}

function leavePermissionHelp(type: PermissionHelpType) {
  clearHelpTimer()
  if (pinnedHelp.value === type) return
  helpTimer = setTimeout(() => {
    if (activeHelp.value === type) closePermissionHelp()
  }, 100)
}

function togglePermissionHelp(type: PermissionHelpType) {
  clearHelpTimer()
  if (pinnedHelp.value === type) {
    closePermissionHelp()
    return
  }
  pinnedHelp.value = type
  activatePermissionHelp(type)
}

function parseDateTime(value: string) {
  const normalized = /T/.test(value) ? value : value.replace(' ', 'T')
  return new Date(normalized)
}

function formatDateTime(value: string) {
  const date = parseDateTime(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (item: number) => String(item).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function remainingLabel(value: string) {
  const remaining = Math.max(0, parseDateTime(value).getTime() - Date.now())
  if (!remaining) return t('enterprise_management_expired')
  const days = Math.floor(remaining / 86_400_000)
  const hours = Math.max(1, Math.ceil((remaining % 86_400_000) / 3_600_000))
  return days
    ? t('enterprise_management_link_remaining_days_hours', { days, hours })
    : t('enterprise_management_link_remaining_hours', { hours })
}

function permissionValue(options: { includeFileEdit?: boolean } = {}) {
  const includeFileEdit = options.includeFileEdit !== false
  let permission = 0
  if (form.fileLook) permission |= EnterpriseMemberPermission.FILE_LOOK
  if (includeFileEdit && form.fileEdit) permission |= EnterpriseMemberPermission.FILE_EDIT
  if (form.fileDownload) permission |= EnterpriseMemberPermission.FILE_DOWNLOAD
  if (form.termUse) permission |= EnterpriseMemberPermission.TERM_USE
  if (form.termEdit) permission |= EnterpriseMemberPermission.TERM_EDIT
  return permission
}

function targetInvitePermissionValue() {
  return permissionValue({ includeFileEdit: false })
}

async function submit() {
  emailAddNotice.value = ''
  if (!isEditing.value && activeMode.value === 'link') {
    if (!generatedInviteUrl.value) return
    await copyInviteLink()
    close()
    return
  }
  if (!canSubmit.value || submitting.value || !form.role) return
  const legacySnapshot = !isEditing.value && isLegacyCross.value ? prepareLegacySnapshot() : null
  if (isLegacyCross.value && !isEditing.value && !legacySnapshot) return
  const submitRevision = dialogRevision
  submitting.value = true
  try {
    const response = isEditing.value && editingMember.value
      ? await saveExistingMember(editingMember.value)
      : await addNewMember(legacySnapshot)
    if (legacySnapshot && !isCurrentLegacySnapshot(legacySnapshot)) return
    if (!legacySnapshot && submitRevision !== dialogRevision) return
    if (response.code !== NetCode.SUCCESS) {
      if (legacySnapshot) {
        clearLegacySnapshot(legacySnapshot)
        Toast(
          String(response.code) === '413'
            ? t('enterprise_management_add_failed')
            : response.message
              || (String(response.code) === TEAM_MEMBER_COUNT_LIMIT_CODE
                ? t('enterprise_management_add_failed')
                : t('enterprise_management_add_failed')),
        )
        return
      }
      if (!isEditing.value && String(response.code) === USER_NOT_FOUND_CODE) {
        emailAddNoticeSwitchable.value = true
        emailAddNotice.value = t('enterprise_management_email_not_registered')
        return
      }
      if (!isEditing.value && String(response.code) === MEMBER_IN_OTHER_TEAM_CODE) {
        emailAddNoticeSwitchable.value = false
        emailAddNotice.value = t('enterprise_management_member_in_other_team')
        return
      }
      Toast(response.message || (isEditing.value ? t('enterprise_management_edit_failed') : t('enterprise_management_add_failed')))
      return
    }
    if (legacySnapshot) {
      handleLegacyMemberResult(response.data as LegacyMemberResult | undefined, legacySnapshot)
      return
    }
    if (!isEditing.value && isLegacyCross.value) {
      Toast(response.message || t('enterprise_management_processing_hint'))
      return
    }
    Toast(isEditing.value ? t('enterprise_management_update_success') : t('enterprise_management_add_success'))
    emit('saved')
    close()
  } catch (error: any) {
    if (legacySnapshot) {
      if (!isCurrentLegacySnapshot(legacySnapshot)) return
      rememberPendingFromResult(legacySnapshot, { requestId: legacySnapshot.requestId }, 'UNKNOWN')
      Toast(t('enterprise_management_processing_hint'))
      return
    }
    if (!legacySnapshot && submitRevision !== dialogRevision) return
    Toast(error?.message || (isEditing.value ? t('enterprise_management_edit_failed') : t('enterprise_management_add_failed')))
  } finally {
    if (legacySnapshot ? isCurrentLegacySnapshot(legacySnapshot) : submitRevision === dialogRevision) submitting.value = false
  }
}

async function createTargetInvite() {
  if (!canGenerateLink.value || submitting.value || !form.region) return
  linkInviteNotice.value = ''
  submitting.value = true
  try {
    const previousInvite = generatedInvite.value || staleGeneratedInvite.value
    if (previousInvite?.id) {
      const revokeResponse = await AccountServer.revokeEnterpriseTargetInvite({
        inviteId: previousInvite.id,
        region: previousInvite.region || form.region,
      })
      if (revokeResponse.code !== NetCode.SUCCESS) {
        Toast(revokeResponse.message || t('enterprise_management_generate_failed'))
        return
      }
      clearGeneratedInvite()
    }
    const response = await AccountServer.createEnterpriseTargetInvite({
      region: form.region,
      inviteAccount: form.email,
      aliasName: '',
      roleType: 3,
      permission: targetInvitePermissionValue(),
    })
    if (String(response.code) === MEMBER_IN_OTHER_TEAM_CODE) {
      clearGeneratedInvite()
      linkInviteNotice.value = t('enterprise_management_member_in_other_team')
      return
    }
    if (response.code !== NetCode.SUCCESS || !response.data) {
      Toast(response.message || t('enterprise_management_generate_failed'))
      return
    }
    const inviteUrl = String(response.data.url || '')
    if (!inviteUrl) {
      Toast(t('enterprise_management_generate_failed'))
      return
    }
    generatedInvite.value = response.data
    generatedInviteKey.value = targetInviteKey()
    Toast(t('enterprise_management_generate_success'))
    emit('saved')
  } catch (error: any) {
    Toast(error?.message || t('enterprise_management_generate_failed'))
  } finally {
    submitting.value = false
  }
}

async function copyInviteLink() {
  if (!generatedInviteUrl.value) return
  try {
    await navigator.clipboard.writeText(generatedInviteUrl.value)
    Toast(t('enterprise_management_copy_success'))
  } catch {
    Toast(t('enterprise_management_copy_failed'))
  }
}

function switchMode(mode: 'email' | 'link') {
  if (submitting.value || checkingStatus.value) return
  closePermissionHelp()
  activeMode.value = mode
  emailAddNotice.value = ''
  linkInviteNotice.value = ''
  clearGeneratedInvite()
  if (mode === 'link') {
    form.role = 3
    form.fileLook = false
    form.fileEdit = false
    form.fileDownload = false
    form.termUse = false
    form.termEdit = false
  } else if (!isEditing.value) {
    form.role = null
    form.fileLook = true
    form.fileEdit = true
    form.fileDownload = true
    form.termUse = true
    form.termEdit = true
  }
}

onBeforeUnmount(() => {
  closePermissionHelp()
})

function addNewMember(legacySnapshot: LegacyMemberSnapshot | null = null) {
  const params = {
    inviteAccount: form.email,
    aliasName: form.alias,
    permission: String(permissionValue()),
    roleType: form.role as number,
  }
  if (isPaidGlobal.value) {
    const contractNo = String(bizTeamInfo.value?.globalContractNo || '').trim()
    if (!contractNo) throw new Error(t('enterprise_management_operation_failed'))
    return GlobalBizServer.addMember({
      ...params,
      contractNo,
      region: form.region as EnterpriseSite,
    })
  }
  if (isLegacyCross.value) {
    if (!legacySnapshot) throw new Error(t('enterprise_management_operation_failed'))
    return AccountServer.addMember({
      ...params,
      region: legacySnapshot.targetSite,
      requestId: legacySnapshot.requestId,
    })
  }
  return AccountServer.addMember(params)
}

function saveExistingMember(member: EditableMember) {
  const params = {
    memberId: member.id,
    aliasName: form.alias,
    permission: String(permissionValue()),
    roleType: canEditRole.value ? form.role : null,
  }
  if (isPaidGlobal.value && form.region !== currentRegion.value) {
    const contractNo = String(bizTeamInfo.value?.globalContractNo || '').trim()
    if (!contractNo) throw new Error(t('enterprise_management_operation_failed'))
    return GlobalBizServer.editMember({
      ...params,
      contractNo,
      region: form.region as EnterpriseSite,
    })
  }
  return AccountServer.editMember(params)
}

function reset() {
  submitting.value = false
  checkingStatus.value = false
  editingMember.value = null
  legacyPending.value = null
  activeMode.value = 'email'
  clearGeneratedInvite()
  emailAddNotice.value = ''
  emailAddNoticeSwitchable.value = true
  linkInviteNotice.value = ''
  closePermissionHelp()
  form.region = ''
  form.email = ''
  form.alias = ''
  form.role = null
  form.fileLook = true
  form.fileEdit = true
  form.fileDownload = true
  form.termUse = true
  form.termEdit = true
}

function close() {
  dialogRevision += 1
  closePermissionHelp()
  visible.value = false
}

function snapshotFromRecord(record: LegacyMemberPendingRecord): LegacyMemberSnapshot {
  const identity = {
    userId: record.userId,
    teamId: record.teamId,
    migrationNo: record.migrationNo,
    site: record.site,
  }
  return {
    revision: dialogRevision,
    identity,
    identityKey: legacyMemberPendingIdentityKey(identity),
    requestId: record.requestId,
    targetSite: record.targetSite,
    createdAt: record.createdAt,
  }
}

function prepareLegacySnapshot(): LegacyMemberSnapshot | null {
  if (!legacyMemberPendingStorageAvailable()) {
    Toast(t('enterprise_management_operation_failed'))
    return null
  }
  const identity = { ...pendingIdentity.value }
  const identityKey = legacyMemberPendingIdentityKey(identity)
  const targetSite = form.region as EnterpriseSite
  if (!identityKey || !targetSite) {
    Toast(t('enterprise_management_operation_failed'))
    return null
  }
  const snapshot: LegacyMemberSnapshot = {
    revision: dialogRevision,
    identity,
    identityKey,
    requestId: createLegacyMemberRequestId(),
    targetSite,
    createdAt: Date.now(),
  }
  const record: LegacyMemberPendingRecord = {
    ...identity,
    requestId: snapshot.requestId,
    targetSite,
    status: 'UNKNOWN',
    createdAt: snapshot.createdAt,
  }
  if (!rememberLegacyMemberPending(record)) {
    Toast(t('enterprise_management_operation_failed'))
    return null
  }
  legacyPending.value = record
  return snapshot
}

function isCurrentLegacySnapshot(snapshot: LegacyMemberSnapshot) {
  return snapshot.revision === dialogRevision
    && snapshot.identityKey === legacyMemberPendingIdentityKey(pendingIdentity.value)
}

function clearLegacySnapshot(snapshot: LegacyMemberSnapshot) {
  clearLegacyMemberPending(snapshot.identity, snapshot.requestId)
  if (isCurrentLegacySnapshot(snapshot)) legacyPending.value = readLegacyMemberPending(snapshot.identity)
}

function rememberPendingFromResult(snapshot: LegacyMemberSnapshot, result: LegacyMemberResult, status: 'RESERVED' | 'UNKNOWN') {
  const record: LegacyMemberPendingRecord = {
    ...snapshot.identity,
    requestId: result.requestId || snapshot.requestId,
    targetSite: snapshot.targetSite,
    status,
    createdAt: snapshot.createdAt,
  }
  if (!rememberLegacyMemberPending(record)) return
  if (!isCurrentLegacySnapshot(snapshot)) return
  legacyPending.value = record
}

function handleLegacyMemberResult(
  result: LegacyMemberResult | null | undefined,
  snapshot: LegacyMemberSnapshot,
  options: LegacyStatusHandlingOptions = {},
): boolean {
  const state = legacyMemberStatusState(result)
  if (state === 'success') {
    clearLegacySnapshot(snapshot)
    if (!isCurrentLegacySnapshot(snapshot)) return true
    if (!options.silent) Toast(t('enterprise_management_add_success'))
    emit('saved')
    if (!options.keepDialogOpen) close()
    return true
  }
  if (state === 'failed') {
    clearLegacySnapshot(snapshot)
    if (!isCurrentLegacySnapshot(snapshot)) return true
    if (!options.silent) {
      Toast(result?.errorCode ? `${t('enterprise_management_add_failed')} (${result.errorCode})` : t('enterprise_management_add_failed'))
    }
    return true
  }
  rememberPendingFromResult(snapshot, result || {}, state === 'pending' ? 'RESERVED' : 'UNKNOWN')
  if (isCurrentLegacySnapshot(snapshot) && !options.silent) Toast(t('enterprise_management_processing_hint'))
  return true
}

async function checkLegacyPendingStatus(options: LegacyStatusHandlingOptions = {}) {
  if (!legacyPending.value || checkingStatus.value) return
  const snapshot = snapshotFromRecord(legacyPending.value)
  checkingStatus.value = true
  try {
    const teamId = Number(snapshot.identity.teamId)
    if (!Number.isFinite(teamId)) throw new Error(t('enterprise_management_operation_failed'))
    const response = await LegacyServer.memberStatus(teamId, snapshot.requestId)
    if (!isCurrentLegacySnapshot(snapshot)) return
    if (response.code !== NetCode.SUCCESS) {
      clearLegacySnapshot(snapshot)
      if (!options.silent) {
        Toast(String(response.code) === '413'
          ? t('enterprise_management_expired')
          : response.message || t('enterprise_management_operation_failed'))
      }
      return
    }
    handleLegacyMemberResult(response.data, snapshot, options)
  } catch (error: any) {
    if (isCurrentLegacySnapshot(snapshot)) {
      rememberPendingFromResult(snapshot, { requestId: snapshot.requestId }, 'UNKNOWN')
      if (!options.silent) Toast(error?.message || t('enterprise_management_operation_failed'))
    }
  } finally {
    if (isCurrentLegacySnapshot(snapshot)) checkingStatus.value = false
  }
}

function restoreLegacyPending() {
  const record = readLegacyMemberPending(pendingIdentity.value)
  if (!record) return
  legacyPending.value = record
  form.region = record.targetSite
}

function open(member?: EditableMember) {
  dialogRevision += 1
  reset()
  if (member) {
    editingMember.value = member
    form.region = member.region || currentRegion.value
    form.email = member.userAccount
    form.alias = member.aliasName || ''
    form.role = member.roleType
    const permission = Number(member.permission || 0)
    form.fileLook = PermissionUtils.hasPermission(permission, EnterpriseMemberPermission.FILE_LOOK)
    form.fileEdit = PermissionUtils.hasPermission(permission, EnterpriseMemberPermission.FILE_EDIT)
    form.fileDownload = PermissionUtils.hasPermission(permission, EnterpriseMemberPermission.FILE_DOWNLOAD)
    form.termUse = PermissionUtils.hasPermission(permission, EnterpriseMemberPermission.TERM_USE)
    form.termEdit = PermissionUtils.hasPermission(permission, EnterpriseMemberPermission.TERM_EDIT)
  } else if (isLegacyCross.value) {
    restoreLegacyPending()
    if (!form.region) form.region = currentRegion.value
  } else if (!isMultiWorkspace.value) {
    form.region = currentRegion.value
  }
  visible.value = true
  // 弹窗重新打开时主动向权威侧收口上一次请求，避免已完成的请求仅因本地缓存而永久锁住表单。
  if (legacyPending.value) {
    void checkLegacyPendingStatus({ silent: true, keepDialogOpen: true })
  }
}

function openInvite(member?: EditableMember) {
  dialogRevision += 1
  reset()
  activeMode.value = 'link'
  form.region = member?.region || currentRegion.value
  form.email = member?.userAccount || ''
  form.role = 3
  form.fileLook = false
  form.fileEdit = false
  form.fileDownload = false
  form.termUse = false
  form.termEdit = false
  visible.value = true
}

defineExpose({ open, openInvite })
</script>

<style scoped lang="scss">
:global(.enterprise-add-member-modal.el-dialog) {
  width: 520px !important;
  max-width: calc(100vw - 32px);
  overflow: visible;
  border-radius: 14px;
  box-shadow: 0 16px 44px rgba(10, 35, 77, .2);
}

:global(.enterprise-add-member-modal.el-dialog .el-dialog__body) {
  padding: 0;
}

.add-member-dialog {
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  width: 520px;
  max-width: 100%;
  padding: 26px;
  color: #1f2329;
  background: #fff;
}

.add-member-dialog__header {
  display: flex;
  width: 100%;
  height: 16px;
  align-items: center;
  justify-content: flex-end;
}

.add-member-dialog__close {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  color: #8f959e;
  background: transparent;
  cursor: pointer;
}

.add-member-dialog h2 { margin: 0; text-align: center; font-family: Inter, sans-serif; font-size: 17px; font-weight: 800; line-height: 25px; }
.add-member-tabs {
  display: flex;
  align-items: flex-end;
  gap: 24px;
  border-bottom: 1px solid #eef1f5;
}
.add-member-tabs button {
  position: relative;
  padding: 0 2px 10px;
  border: 0;
  color: #51565d;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}
.add-member-tabs button::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: transparent;
  content: '';
}
.add-member-tabs button.active { color: #2b7de9; font-weight: 700; }
.add-member-tabs button.active::after { background: #2b7de9; }
.form-field { display: grid; grid-template-columns: 100px minmax(0, 1fr); align-items: center; gap: 10px; }
.form-field > label { justify-self: end; color: #51565d; font-size: 13px; }
.form-field > label i { margin-right: 2px; color: #e5484d; font-style: normal; }
.form-field > input,
.locked-space {
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #d9dee5;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
}

.form-field > input { padding: 0 12px; color: #1f2329; background: #fff; }
.form-field > input:disabled { color: #51565d; background: #f2f4f7; cursor: not-allowed; }
.form-field > input::placeholder { color: #98a2b3; }
.form-field > input:focus { border-color: #2b7de9; box-shadow: 0 0 0 2px rgba(43, 125, 233, .08); }

.locked-space { display: flex; align-items: center; justify-content: space-between; padding: 0 12px; color: #51565d; background: #f2f4f7; }
.locked-space span { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
.locked-space > .workbench-icon { color: #c3c9d1; }
.space-hint { margin: 0 0 0 84px; color: #8f959e; font-size: 11px; line-height: 1.55; }
.legacy-pending-hint { color: #2b7de9; }
.legacy-pending-hint button { margin-left: 8px; border: 0; color: #2b7de9; background: transparent; font-size: 11px; font-weight: 700; cursor: pointer; }
.legacy-pending-hint button:disabled { color: #98a2b3; cursor: not-allowed; }

.member-field-control { width: 100%; }
.member-field-control :deep(.el-select__wrapper) { min-height: 38px; padding: 0 12px; border-radius: 8px; box-shadow: 0 0 0 1px #d9dee5 inset; }
.member-field-control :deep(.el-select__placeholder) { color: #98a2b3; font-size: 13px; }
.member-field-control :deep(.el-select__selected-item) { font-size: 13px; }
.member-field-control :deep(.el-select__caret) { width: 13px; height: 13px; color: #8f959e; }
.member-field-control :deep(.el-select__wrapper.is-focused) { box-shadow: 0 0 0 1px #2b7de9 inset; }

.permission-fields { display: flex; flex-direction: column; gap: 10px; }
.email-add-panel { display: flex; flex-direction: column; gap: 16px; }
.permission-row { display: grid; grid-template-columns: 100px minmax(0, 1fr); align-items: center; gap: 10px; min-height: 19px; }
.permission-row__label { justify-self: end; color: #51565d; font-size: 13px; line-height: 19px; }
.permission-row__body { display: flex; min-width: 0; flex-wrap: wrap; align-items: center; gap: 8px 14px; }
.permission-row__name { display: inline-flex; min-width: 62px; max-width: 84px; flex: 0 0 auto; align-items: center; gap: 4px; color: #1f2329; font-size: 13px; line-height: 19px; white-space: nowrap; }
.permission-option { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 6px; color: #1f2329; font-size: 12.5px; line-height: 18px; cursor: pointer; }
.permission-option:has(input:disabled) { color: #8f959e; cursor: not-allowed; }
.permission-option input { position: absolute; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.permission-option__checkbox {
  display: inline-flex;
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  border: 1px solid #c3c9d1;
  border-radius: 3px;
  color: transparent;
  background: #fff;
}
.permission-option input:checked + .permission-option__checkbox { border-color: #2b7de9; color: #fff; background: #2b7de9; }
.permission-option input:focus-visible + .permission-option__checkbox { box-shadow: 0 0 0 2px rgba(43, 125, 233, .14); }

.email-add-notice { margin: 0 0 0 84px; }
.email-add-notice__message { display: flex; align-items: flex-start; gap: 6px; color: #92400e; font-size: 11px; line-height: 1.5; }
.email-add-notice__message .workbench-icon { margin-top: 2px; flex: 0 0 auto; color: #d97706; }
.email-add-notice button { margin: 4px 0 0 18px; padding: 0; border: 0; color: #2b7de9; background: transparent; font: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.email-add-rule { margin: 0; color: #8f959e; font-size: 11px; line-height: 1.5; }
.link-invite-notice { margin: -8px 0 0; }

.link-invite-panel { display: flex; flex-direction: column; gap: 16px; }
.link-invite-description,
.link-invite-notes { margin: 0; color: #51565d; font-size: 12.5px; line-height: 1.55; }
.link-invite-notes { color: #8f959e; font-size: 11px; line-height: 1.5; }
.link-email-row,
.generated-link { display: flex; gap: 8px; }
.link-email-row input,
.generated-link input {
  min-width: 0;
  height: 38px;
  flex: 1;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid #d9dee5;
  border-radius: 8px;
  outline: none;
  color: #1f2329;
  background: #fff;
  font-size: 13px;
}
.generated-link input { color: #51565d; background: #f7f9fc; }
.link-email-row input:focus { border-color: #2b7de9; box-shadow: 0 0 0 2px rgba(43, 125, 233, .08); }
.link-email-row input:disabled { color: #8f959e; background: #f2f4f7; cursor: not-allowed; }
.link-email-row button,
.generated-link button {
  height: 38px;
  flex: 0 0 auto;
  padding: 0 14px;
  border: 1px solid #2b7de9;
  border-radius: 8px;
  color: #2b7de9;
  background: #fff;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.link-email-row button:disabled { border-color: #c9d9ee; color: #9bbce5; cursor: not-allowed; }
.generated-link button { padding: 0 16px; border-color: #2b7de9; color: #fff; background: #2b7de9; }
.generated-link-block { display: flex; flex-direction: column; gap: 8px; }
.generated-link-meta { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.generated-link-meta > span { display: inline-flex; min-width: 0; align-items: center; gap: 5px; color: #d97706; font-size: 11px; line-height: 1.5; }
.generated-link-meta > button { flex: 0 0 auto; padding: 0; border: 0; color: #2b7de9; background: transparent; font: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.generated-link-meta > button:disabled { color: #98a2b3; cursor: not-allowed; }

.save-button {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 10px;
  color: #fff;
  background: #2b7de9;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.save-button:disabled { background: #dcebfb; cursor: not-allowed; }

@media (max-width: 540px) {
  .add-member-dialog { padding: 22px 18px; }
  .form-field,
  .permission-row { grid-template-columns: 82px minmax(0, 1fr); gap: 8px; }
  .space-hint,
  .email-add-notice { margin-left: 62px; }
  .permission-row__body { gap: 8px 10px; }
  .link-email-row { align-items: stretch; }
}
</style>

<style lang="scss">
.enterprise-member-select-popper.el-select__popper {
  overflow: hidden;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(31, 35, 41, .15);
}

.enterprise-member-select-popper .el-popper__arrow { display: none; }
.enterprise-member-select-popper .el-select-dropdown { padding: 6px; }
.enterprise-member-select-popper .el-select-dropdown__list { padding: 0; }
.enterprise-member-select-popper .el-select-dropdown__item {
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  color: #1f2329;
  font-size: 12.5px;
  line-height: 34px;
}
.enterprise-member-select-popper .el-select-dropdown__item.is-hovering { background: #f5f9ff; }
.enterprise-member-select-popper .el-select-dropdown__item.is-selected { color: #2b7de9; background: #f5f9ff; font-weight: 700; }
.enterprise-member-select-popper .el-select-dropdown__item.is-selected::after { display: none; }
.enterprise-member-select-option { display: flex; height: 100%; align-items: center; gap: 8px; }
.enterprise-member-select-option > span:nth-child(2) { flex: 1; }
.enterprise-member-select-option > .workbench-icon:first-child { color: #8f959e; }
.enterprise-member-select-popper .el-select-dropdown__item.is-selected .enterprise-member-select-option > .workbench-icon:first-child,
.enterprise-member-select-option > span + .workbench-icon { color: #2b7de9; }
</style>
